/**
 * Custom Scroll Thumb Hook
 *
 * Provides a custom scroll thumb indicator for scrollable elements.
 * Deferred initialization to avoid blocking initial render.
 */

import { useEffect, useRef } from "react";

interface ScrollThumbConfig {
  brightness?: number;
  enabled?: boolean;
}

/**
 * Hook to manage custom scroll thumb indicators
 * Deferred to avoid blocking initial render
 */
export function useScrollThumb(config: ScrollThumbConfig = {}) {
  const { brightness = 0.6, enabled = true } = config;
  const initializedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    // Defer initialization to avoid blocking initial render
    const initTimeout = setTimeout(() => {
      if (initializedRef.current) return;
      initializedRef.current = true;

      const normalizedBrightness = Math.max(
        0.05,
        Math.min(1, Number(brightness) || 0.6),
      );
      const baseColorAlpha = Number(
        (0.08 + normalizedBrightness * 0.32).toFixed(3),
      );
      const activeColorAlpha = Math.min(
        0.95,
        Number((baseColorAlpha + normalizedBrightness * 0.25).toFixed(3)),
      );
      const activeOpacity = Number(
        (0.25 + normalizedBrightness * 0.55).toFixed(3),
      );
      // idleOpacity reserved for future use
      const boxShadowAlpha = Number(
        (0.08 + normalizedBrightness * 0.22).toFixed(3),
      );

      const html = document.documentElement;
      const managed = new Map<Element, { dispose: () => void }>();
      const SELECTOR =
        "[data-overlay-scroll], .custom-scrollbar, [data-scroll-overlay]";

      let cachedWindowWidth = window.innerWidth;
      let cachedWindowHeight = window.innerHeight;

      const createThumbElement = (): HTMLDivElement => {
        const thumb = document.createElement("div");
        thumb.setAttribute("data-scroll-thumb", "true");
        Object.assign(thumb.style, {
          position: "fixed",
          width: "6px",
          height: "48px",
          borderRadius: "9999px",
          background: `rgba(197, 157, 95, ${baseColorAlpha})`,
          opacity: "0",
          visibility: "hidden",
          transform: "translate3d(0, 0, 0)",
          transition: "opacity 160ms ease-out, visibility 160ms ease-out",
          zIndex: "9999",
          pointerEvents: "none",
          boxShadow: `0 6px 18px rgba(197, 157, 95, ${boxShadowAlpha})`,
          willChange: "transform, opacity",
        });
        document.body.appendChild(thumb);
        return thumb;
      };

      const ensureManager = (el: Element) => {
        if (managed.has(el)) return;

        const isDocumentTarget = el === html;
        const scrollElement = isDocumentTarget ? window : (el as HTMLElement);
        const thumb = createThumbElement();

        let hideTimer: number | null = null;
        let scheduled = false;

        const updateThumb = () => {
          scheduled = false;
          const scrollHeight = isDocumentTarget
            ? html.scrollHeight
            : (el as HTMLElement).scrollHeight;
          const viewport = isDocumentTarget
            ? cachedWindowHeight
            : (el as HTMLElement).clientHeight;
          const scrollTop = isDocumentTarget
            ? window.scrollY
            : (el as HTMLElement).scrollTop;
          const scrollable = scrollHeight - viewport;

          if (viewport <= 0 || scrollable <= 1) {
            thumb.style.opacity = "0";
            thumb.style.visibility = "hidden";
            return;
          }

          const ratio = Math.min(1, Math.max(0, scrollTop / scrollable));
          const rect = isDocumentTarget
            ? { top: 0, right: cachedWindowWidth, height: cachedWindowHeight }
            : (el as HTMLElement).getBoundingClientRect();
          const computedHeight = Math.max(
            32,
            (viewport / scrollHeight) * rect.height,
          );
          const maxOffset = rect.height - computedHeight;
          const offset = ratio * maxOffset;

          thumb.style.height = `${computedHeight}px`;
          thumb.style.top = `${Math.max(0, rect.top + offset)}px`;
          thumb.style.left = `${rect.right - 6}px`;
        };

        const revealThumb = () => {
          if (!scheduled) {
            scheduled = true;
            requestAnimationFrame(updateThumb);
          }

          thumb.style.visibility = "visible";
          thumb.style.opacity = String(activeOpacity);
          thumb.style.background = `rgba(197, 157, 95, ${activeColorAlpha})`;

          if (hideTimer) clearTimeout(hideTimer);
          hideTimer = window.setTimeout(() => {
            thumb.style.opacity = "0";
            hideTimer = window.setTimeout(() => {
              thumb.style.visibility = "hidden";
            }, 160);
          }, 600);
        };

        const handleScroll = () => requestAnimationFrame(revealThumb);

        scrollElement.addEventListener("scroll", handleScroll, {
          passive: true,
        });

        const cleanup = () => {
          scrollElement.removeEventListener("scroll", handleScroll);
          if (hideTimer) clearTimeout(hideTimer);
          thumb.remove();
          managed.delete(el);
        };

        managed.set(el, { dispose: cleanup });
      };

      const scan = () => {
        const elements = new Set<Element>([
          html,
          ...document.querySelectorAll(SELECTOR),
        ]);
        elements.forEach(ensureManager);
        managed.forEach((manager, el) => {
          if (!elements.has(el)) manager.dispose();
        });
      };

      // Throttled resize handler
      let resizeScheduled = false;
      const handleResize = () => {
        if (resizeScheduled) return;
        resizeScheduled = true;
        requestAnimationFrame(() => {
          resizeScheduled = false;
          cachedWindowWidth = window.innerWidth;
          cachedWindowHeight = window.innerHeight;
        });
      };

      window.addEventListener("resize", handleResize, { passive: true });

      // Throttled mutation observer
      let mutationScheduled = false;
      const mutationObserver = new MutationObserver(() => {
        if (mutationScheduled) return;
        mutationScheduled = true;
        requestAnimationFrame(() => {
          mutationScheduled = false;
          scan();
        });
      });
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Initial scan
      scan();

      cleanupRef.current = () => {
        mutationObserver.disconnect();
        window.removeEventListener("resize", handleResize);
        managed.forEach((manager) => manager.dispose());
        managed.clear();
      };
    }, 1000); // Defer 1 second after mount

    return () => {
      clearTimeout(initTimeout);
      cleanupRef.current?.();
      initializedRef.current = false;
    };
  }, [brightness, enabled]);
}
